import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import { FileType, Guid, VoidResponse } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ListInvoiceBatchImagesRequest,
    ListInvoiceBatchImagesService,
    SendMobileUploadLinkRequest
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { Color, forLifeOf, LifecycleAware, Masks, Unmasks } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService, ModalWorkflowService } from '@lsq-frontend/common-ui';
import { Observable } from 'rxjs';
import { ProgressState } from 'rxjs-uploader';
import { first, takeWhile } from 'rxjs/operators';
import {
    AdditionalUploadType,
    IBatchUploaderData,
    InvoiceFileUpload
} from '../../invoices/invoices.models';
import { InvoiceBatchService } from '../../invoices/upload-invoices/invoice-batch.service';
import { SmsUploadService } from '../../invoices/upload-invoices/sms-upload.service';
import { UploadInvoicesService } from '../../invoices/upload-invoices/upload-invoices.service';

@LifecycleAware()
@Component({
    selector: 'lsq-uploads-preview-modal',
    template: `
        <div class="modal-content">
            <div class="modal-header">
                <h1>
                    Additional Uploads
                </h1>
                <lsq-button
                    *ngIf="!isSubmitDisabled"
                    appearance="transparent"
                    class="close-button"
                    (buttonClick)="closeModal()"
                >
                    <lsq-icon [type]="icons.Close" [color]="color.Black"> </lsq-icon>
                </lsq-button>
            </div>
            <ng-container *ngIf="mobileUploadPending; else filePreview">
                <div class="mobile-header">
                    <div class="text-center">
                        <p *ngIf="mobileLinkSent">
                            The upload link has been sent to
                            <strong>{{ phoneNumber | phone }}</strong
                            >. The link will remain active for the next 10 minutes.<br />Any images
                            uploaded will automatically be added to your batch and appear on this
                            page.
                        </p>
                        <div class="mtb-2" id="enter-mobile-phone-number">
                            <form
                                id="mobile-phone-form"
                                class="d-inline-block align-middle"
                                [formGroup]="mobileFormGroup"
                            >
                                <lsq-input
                                    label="Mobile Phone Number"
                                    name="phone"
                                    [group]="mobileFormGroup"
                                    [mask]="masks.PhoneMask"
                                    type="tel"
                                    [unmask]="phoneUnmask"
                                    formControlName="phone"
                                >
                                </lsq-input>
                                <lsq-button
                                    type="submit"
                                    [disabled]="!canSendLink || !!mobileFormGroup.invalid"
                                    [group]="mobileFormGroup"
                                    (buttonClick)="sendSmsMessage()"
                                >
                                    {{ mobileLinkSent ? 'Resend Link' : 'Send Link' }}
                                </lsq-button>
                            </form>
                        </div>

                        <lsq-button
                            size="lg"
                            appearance="transparent"
                            (buttonClick)="fileInputElement.click()"
                        >
                            Upload files from your computer
                        </lsq-button>
                    </div>
                </div>
            </ng-container>
            <div class="modal-footer">
                <div class="modal-footer-left"></div>
                <div class="modal-footer-right">
                    <ng-container *ngIf="!parentFileUpload && fileUploads.length !== 0">
                        <lsq-button
                            size="sm"
                            [disabled]="isSubmitDisabled"
                            [submitting]="isSubmittingInvoiceBatch"
                            (buttonClick)="submitInvoiceBatch()"
                        >
                            Submit
                        </lsq-button>
                    </ng-container>
                    <ng-container *ngIf="!!parentFileUpload">
                        <lsq-button
                            size="sm"
                            [disabled]="isSubmitDisabled"
                            (buttonClick)="closeModal()"
                        >
                            Done
                        </lsq-button>
                    </ng-container>
                </div>
            </div>
            <ng-template #filePreview>
                <div
                    class="uploads-preview-layout"
                    [ngClass]="{
                        disabled: isSubmittingInvoiceBatch
                    }"
                >
                    <div class="left-side">
                        <invoice-preview
                            *ngIf="fileUploads[selectedImageIndex]?.isUploadStatusComplete"
                            [imageId]="fileUploads[selectedImageIndex]?.imageKey"
                            [filename]="fileUploads[selectedImageIndex]?.name"
                            [totalPages]="fileUploads[selectedImageIndex]?.pages"
                        >
                        </invoice-preview>
                    </div>
                    <div class="right-side">
                        <additional-uploads-row-item
                            *ngFor="let fileUpload of fileUploads; let i = index"
                            [fileUpload]="fileUpload"
                            [isSelected]="i === selectedImageIndex"
                            (onView)="selectedImageIndex = i"
                            (onDelete)="removeFileUpload(fileUpload)"
                        >
                        </additional-uploads-row-item>
                        <div [style.margin.rem]="1">
                            <lsq-button
                                (buttonClick)="fileInputElement.click()"
                                appearance="transparent"
                            >
                                <lsq-icon class="upload-icon" [type]="icons.Upload"></lsq-icon>
                                Upload More Supporting Documents
                            </lsq-button>
                        </div>
                        <div>
                            <form
                                id="mobile-phone-form"
                                class="align-middle"
                                [formGroup]="mobileFormGroup"
                            >
                                <lsq-input
                                    label="Mobile Phone Number"
                                    name="phone"
                                    [group]="mobileFormGroup"
                                    [mask]="masks.PhoneMask"
                                    type="tel"
                                    [unmask]="phoneUnmask"
                                    formControlName="phone"
                                >
                                </lsq-input>
                                <lsq-button
                                    type="submit"
                                    [disabled]="!canSendLink || !!mobileFormGroup.invalid"
                                    [group]="mobileFormGroup"
                                    (buttonClick)="sendSmsMessage()"
                                >
                                    {{ mobileLinkSent ? 'Resend Link' : 'Send Link' }}
                                </lsq-button>
                            </form>
                            <p *ngIf="mobileLinkSent" class="mb-4">
                                The upload link has been sent to
                                <strong>{{ phoneNumber | phone }}</strong
                                >. The link will remain active for the next 10 minutes. Any images
                                uploaded will automatically be added to your batch and appear on
                                this page.
                            </p>
                        </div>
                    </div>
                </div>
            </ng-template>
        </div>
    `,
    styleUrls: ['uploads-preview-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UploadsPreviewModalComponent implements OnInit, OnDestroy {
    public icons = Icons;
    public masks = Masks;
    public color = Color;
    public phoneUnmask = Unmasks.PhoneUnmask;
    public progressState = ProgressState;
    public fileUploadsStream: Observable<InvoiceFileUpload[]>;
    public fileUploads: InvoiceFileUpload[] = [];
    public fileInputElement: HTMLInputElement;
    public selectedImageIndex = 0;
    public masterInvoiceBatchKey: Guid;
    public invoiceBatchKey: Guid;
    public isSubmittingInvoiceBatch = false;
    public mobileUpload = false;
    public onSuccess: () => any;

    // Uploader config.
    public allowedContentTypes: string;
    public parentFileUpload: InvoiceFileUpload;
    public invoiceFileUploads: InvoiceFileUpload[] = [];
    public imagePollInterval: any;

    public mobileLinkSent = false;
    public canSendLink = true;
    public phoneNumber: string;
    public mobileFormGroup = new FormGroup({
        phone: new FormControl('', [Validators.required, LsqValidators.phone])
    });

    private _isModalActive = false;

    constructor(
        public modalService: ModalService,
        public workflowService: ModalWorkflowService,
        public uploadInvoicesService: UploadInvoicesService,
        public invoiceBatchService: InvoiceBatchService,
        private _alertService: AlertService,
        private _appSettings: AppSettings,
        private _smsUploadService: SmsUploadService,
        private _listInvoiceBatchImagesService: ListInvoiceBatchImagesService,
        private _userService: UserService
    ) {}

    public async ngOnInit(): Promise<void> {
        let additionalFileUploadsRef: IBatchUploaderData;
        this.allowedContentTypes = this.uploadInvoicesService.allowedContentTypesForInvoices.join(
            ','
        );
        const modalData = this.modalService.modal.data;
        if (modalData) {
            this.onSuccess = modalData.onSuccess;
            this.parentFileUpload = modalData.parentFileUpload;
            this.masterInvoiceBatchKey = modalData.masterInvoiceBatchKey;
            this.invoiceBatchKey =
                modalData.invoiceBatchKey != null
                    ? modalData.invoiceBatchKey
                    : this.invoiceBatchService.invoiceBatchKey;
            this.mobileUpload = modalData.mobileUpload;
            this.phoneNumber = modalData.phoneNumber;
            this.mobileLinkSent = this.mobileUpload ? true : false;
            this.mobileFormGroup.get('phone').setValue(modalData.phoneNumber);
            this._isModalActive = true;

            if (!this.imagePollInterval && this.mobileUpload) {
                this.listenForInvoiceBatchImages();
            }

            if (this.parentFileUpload) {
                this.invoiceBatchKey = this.invoiceBatchService.invoiceBatchKey;
                additionalFileUploadsRef = await this.uploadInvoicesService.getOrCreateAdditionalUploads(
                    AdditionalUploadType.InvoiceBatchImage,
                    this.parentFileUpload.imageKey,
                    this.invoiceBatchKey,
                    () => null,
                    this._smsUploadService.getCompletedUploadsStream()
                );
            }

            if (this.masterInvoiceBatchKey) {
                additionalFileUploadsRef = await this.uploadInvoicesService.getOrCreateAdditionalUploads(
                    AdditionalUploadType.InvoiceBatch,
                    this.masterInvoiceBatchKey,
                    this.invoiceBatchKey,
                    () => null,
                    this._smsUploadService.getCompletedUploadsStream(this.masterInvoiceBatchKey)
                );
            }

            if (additionalFileUploadsRef) {
                const { fileUploadsStream, inputElement } = additionalFileUploadsRef;
                this.fileInputElement = inputElement;
                this.fileUploadsStream = fileUploadsStream;
            }
        }

        this.fileUploadsStream
            .pipe(
                takeWhile(() => this._isModalActive),
                forLifeOf(this)
            )
            .subscribe((fileUploads) => {
                if (this.parentFileUpload) {
                    this.fileUploads = fileUploads.filter(
                        (file: InvoiceFileUpload) =>
                            file.imageKey !== this.parentFileUpload.imageKey
                    );
                    this.parentFileUpload.linkedFileUploads = this.fileUploads;
                } else {
                    this.fileUploads = fileUploads;
                }

                // If a user is uploading a file through Desktop and has no files left on preview, go back to the first modal.
                if ((!this.fileUploads || !this.fileUploads.length) && !this.mobileUpload) {
                    this._reset();
                    this.workflowService.previousStep();
                }
            });
    }

    public ngOnDestroy(): void {
        this._reset();
    }

    public get isSubmitDisabled(): boolean {
        return (
            this.fileUploads.length === 0 ||
            this.fileUploads.some(
                (fileUpload) =>
                    !fileUpload.isUploadStatusComplete ||
                    fileUpload.linkedFileUploads.some(
                        (linkedFileUpload) => !linkedFileUpload.isUploadStatusComplete
                    )
            )
        );
    }

    public get mobileUploadPending(): boolean {
        return !this.shouldShowFilePreview && this.mobileUpload;
    }

    public get shouldShowFilePreview(): boolean {
        return this.fileUploads.length !== 0;
    }

    public closeModal(): void {
        this._reset();
        this.modalService.closeModal();
    }

    public listenForInvoiceBatchImages(): void {
        const listRequest = new ListInvoiceBatchImagesRequest({
            invoiceBatchKey: this.invoiceBatchKey,
            width: 330,
            format: FileType.Jpg
        });

        this.imagePollInterval = setInterval(() => {
            this._listInvoiceBatchImagesService.execute(listRequest);
        }, this._appSettings.mobileUploadPollingInterval);
    }

    public async submitInvoiceBatch(): Promise<void> {
        this.isSubmittingInvoiceBatch = true;
        await this.uploadInvoicesService.submitInvoiceBatch(
            this.fileUploads,
            this.invoiceBatchKey,
            true
        );
        this.isSubmittingInvoiceBatch = false;

        // We keep track of each linked batch relationship in 2 maps (one for each type of additional
        // uploads) in `UploadInvoicesService`.
        // Once we've submitted one of these linked batches, delete it from the map.
        if (this.parentFileUpload) {
            this.uploadInvoicesService.invoiceBatchImageAdditionalUploadsMap.delete(
                this.parentFileUpload.imageKey
            );
        }
        if (this.masterInvoiceBatchKey) {
            this.uploadInvoicesService.invoiceBatchAdditionalUploadsMap.delete(
                this.masterInvoiceBatchKey
            );
        }

        if (typeof this.onSuccess === 'function') {
            this.onSuccess();
        }
        this._alertService.success('You successfully uploaded additional documents.');
        this.closeModal();
    }

    public async removeFileUpload(fileUpload: InvoiceFileUpload): Promise<VoidResponse> {
        return this.invoiceBatchService.removeFileFromInvoiceBatch(
            fileUpload,
            this.invoiceBatchKey
        );
    }

    public async sendSmsMessage(): Promise<void> {
        this.mobileUpload = true;
        this.phoneNumber = this.mobileFormGroup.value.phone;
        // Limit the user from spam sending multiple links to the same number.
        this.canSendLink = false;
        setTimeout(() => (this.canSendLink = true), 5000);

        const user = await this._userService.user$.pipe(first()).toPromise();
        const request = new SendMobileUploadLinkRequest({
            clientName: user.currentCompany.name,
            invoiceBatchKey: this.invoiceBatchKey,
            phone: this.phoneNumber
        });
        this._smsUploadService.sendMessage(request);
        this.mobileLinkSent = true;
        this.workflowService.updateModals({
            data: {
                ...this.modalService.modal.data,
                invoiceBatchKey: this.invoiceBatchKey,
                mobileUpload: this.mobileUpload,
                phoneNumber: this.phoneNumber
            }
        });
        if (!this.imagePollInterval) {
            this.listenForInvoiceBatchImages();
        }
    }

    private _reset(): void {
        this._isModalActive = false;
        this.mobileLinkSent = false;
        clearInterval(this.imagePollInterval);
        delete this.imagePollInterval;
    }
}
